import { Typography, Box, IconButton, FormControl, Select, MenuItem } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { startTransaction, linkCard } from "../../../api/WalletAPI";
import { handleError } from "../../../helpers/Fetchers/fetchers";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from 'swr';
import { PATH_NAME } from "../../../configs/pathName";
import { generalFetcher } from "../../../helpers/Fetchers/fetchers";
import { typo } from "../../../assets/fonts/font";
import colorConfigs from "../../../configs/colorConfigs";
import PayNowPaymentOption from "./PayNowPaymentOption";
import assets from "../../../assets";
import { LinkCardPayload } from "../../../models/IWallet";

const clickBoxStyles = {
  transition: "all 0.2s ease-in-out",
  boxShadow: "0px 4px 6px var(--color-shadow-10)", // Soft shadow for depth

  "&:hover": {
      backgroundColor: colorConfigs.quaternary, // Change color slightly
      boxShadow: "0px 6px 10px var(--color-shadow-20)", // Stronger shadow on hover
      transform: "translateY(-2px)", // Lift effect
  },

  "&:active": {
      boxShadow: "0px 2px 4px var(--color-shadow-20)", // Reduce shadow when pressed
      transform: "translateY(1px)", // Press-down effect
  }
};

const topupStyles = {
  inactiveBox: {
    borderRadius: 4,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "50%",
    padding:{xs:1, sm:1},
    backgroundColor: colorConfigs.quaternary,
    cursor: "pointer",
    ...clickBoxStyles
  },
  activeBox: {
    borderRadius: 4,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "50%",
    padding:{xs:1, sm:1},
    backgroundColor: colorConfigs.quaternary,
    cursor: "pointer",
    border: `2px solid ${colorConfigs.primary}`,
    ...clickBoxStyles
  },
  buttonImage: {
    width: {
      xs: 120,
      sm: 120,
    },
    height: {
      xs: 50,
      sm: 50,
    },
    margin: {
      xs: 1,
    },
    objectFit: "contain",
  }
};

const TopUpPage = () => {
  const [error, setError] = useState<string | null>(null);
  const [transactionRef, setTransactionRef] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("paynow");
  const [amount, setAmount] = useState<number>(5);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const { data: paymentStatus } = useSWR(
    transactionId ? PATH_NAME.API_GET_TRANSACTION_PAYMENT(transactionId) : null, generalFetcher, {refreshInterval:3000}
  );
  const { data: storeCreditsResponse } = useSWR(PATH_NAME.API_STORE_CREDITS_BALANCE, generalFetcher);
  const storeCredits = storeCreditsResponse?.balance;

  useEffect(() => {
    const initializeTransaction = async () => {
      try {
        const transactionResponse = await startTransaction();
        setTransactionRef(transactionResponse.transaction_ref);
        setTransactionId(transactionResponse.id);
      } catch (error) {
        handleError(error, "Failed to start transaction.");
        setError("Failed to start transaction. Please try again.");
      }
    };
    initializeTransaction();
  }, []);

  // Handle 2c2p payment when payment method changes to 2c2p
  useEffect(() => {
    const handle2c2pPayment = async () => {
      if (paymentMethod === "2c2p" && transactionRef && !isLoading) {
        setIsLoading(true);
        setError(null);
        try {
          const transactionRef2c2p = `topup-${transactionRef}`;
          const linkCardPayload: LinkCardPayload = {
            name: transactionRef2c2p,
            invoiceNo: transactionRef2c2p,
            description: "crm top up",
            amount: amount,
            payment_type: "one_time",
          };
          const response = await linkCard(linkCardPayload);
          const webPaymentUrl = response.result?.webPaymentUrl;
          if (webPaymentUrl) {
            window.location.href = webPaymentUrl;
          } else {
            setError("Payment URL not found in the response");
            setIsLoading(false);
          }
        } catch (error) {
          setError("Error during Link Card process");
          setIsLoading(false);
        }
      }
    };

    handle2c2pPayment();
  }, [paymentMethod, transactionRef, amount, isLoading]);

  const displayPaymentMethods = () : React.ReactNode => {
    if(paymentMethod === "paynow") {
      return <PayNowPaymentOption transactionId={transactionId} transactionRef={transactionRef} amount={amount} />
    }
    // Don't render anything for 2c2p as it auto-redirects
    return null;
  }

  const changePaymentMethod = (method: "paynow" | "2c2p") => {
    setPaymentMethod(method);
  }

  const isActiveBox = (method: "paynow" | "2c2p") => {
    if(paymentMethod === method) {
      return topupStyles.activeBox;
    }else {
      return topupStyles.inactiveBox;
    }
  }

  useEffect(() => {
    if (paymentStatus?.payment_complete) {
      navigate(PATH_NAME.PAYMENT_SUCCESS, { replace: true });
    }
  }, [paymentStatus, navigate]);

  return (
    <Box>
      <Box>
        <IconButton onClick={() => navigate(PATH_NAME.MEMBER, { replace: true })}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{...typo.sectionHeadline}} gutterBottom textAlign="center">
          Top Up Wallet
        </Typography>
        <Typography variant="h6" sx={{...typo.title}} gutterBottom textAlign="center">
          Current Balance: SGD {storeCredits !== undefined ? storeCredits : '0'}
        </Typography>

        {/* Amount Dropdown */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <FormControl sx={{ minWidth: 120 }}>
            <Select
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              size="small"
              sx={{
                ...typo.bodyContent,
                textAlign: 'center',
                '& .MuiSelect-select': {
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: colorConfigs.secondary,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colorConfigs.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colorConfigs.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colorConfigs.primary,
                },
              }}
            >
              <MenuItem sx={{ ...typo.bodyContent}} value={5}>$5</MenuItem>
              <MenuItem sx={{ ...typo.bodyContent}} value={10}>$10</MenuItem>
              <MenuItem sx={{ ...typo.bodyContent}} value={20}>$20</MenuItem>
              <MenuItem sx={{ ...typo.bodyContent}} value={50}>$50</MenuItem>
              <MenuItem sx={{ ...typo.bodyContent}} value={100}>$100</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, mx: 'auto', width: { xs: '100%', sm: '50%'}, }}>
          <Box sx={isActiveBox("paynow")} onClick={()=>{changePaymentMethod("paynow")}}>
            <Box component="img" src={assets.images.payNowBtn} alt="wallet" sx={topupStyles.buttonImage} />
          </Box>
          <Box sx={isActiveBox("2c2p")} onClick={()=>{changePaymentMethod("2c2p")}}>
            <Box component="img" src={assets.images.paymentButton} alt="wallet" sx={topupStyles.buttonImage} />
          </Box>
        </Box>

        {error && <Typography color="error" sx={{ textAlign: 'center', mb: 2 }}>{error}</Typography>}
        {isLoading && paymentMethod === "2c2p" && (
          <Typography sx={{ textAlign: 'center', mb: 2 }}>Redirecting to payment page...</Typography>
        )}
        {displayPaymentMethods()}

      </Box>
    </Box>
  );
};

export default TopUpPage;
